import { Add, ContentCopy, Delete, Edit, FilterAlt, Refresh } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import DataGrid from '../../../../components/DataGrid';
import ContentTitle from "../../../../components/Content/ContentTitle";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import { useEffect, useCallback } from "react";
import GovernanceItemService from "../../../../services/GovernanceItemService";
import FormHelper from "../../../../helpers/FormHelper";
import RegistrationStatus from "../../../../components/RegistrationStatus";
import AddEditDialog from "./AddEditDialog";
import { useAuthContext } from "../../../../contexts/AuthContext";
import RemoveDialog from "./RemoveDialog";
import DefaultConstants from "../../../../data/Constants";
import IGovernanceItem from "../../../../interfaces/IGovernanceItem";
import { INavigationLink } from "../../../../interfaces/INavigation";
import Filters from "./Filters";
import { RegistrationStatusEnum } from "../../../../data/Enums";
import Tooltip from "../../../../components/Tooltip";
import useDrawer from "../../../../hooks/useDrawer";
import useDialog from "../../../../hooks/useDialog";
import useState from "../../../../hooks/useState";


interface IStateValues {
    filters: FormData | undefined;
    data: IGovernanceItem[];
    selectedRow: IGovernanceItem | undefined;
}


const GovernanceItem = (linkProps: INavigationLink) => {
    const { translate } = useLanguageContext();
    const { hasPermission, enterprise } = useAuthContext();

    const state = useState<IStateValues>({
        filters: undefined,
        data: [],
        selectedRow: undefined,
    });

    const filtersDrawer = useDrawer();
    const addEditDialog = useDialog();
    const removeDialog = useDialog();


    useEffect(() => {
        handleRefresh();
        // eslint-disable-next-line
    }, [state.values.filters]);
    
    
    const handleRefresh = useCallback(() => {
        const formData = state.values.filters ?? FormHelper.Create(undefined);

        GovernanceItemService.List(formData)
            .then((resp) => {
                state.update('data', resp.data.governanceItems as IGovernanceItem[]);
            });
        
        // eslint-disable-next-line
    }, [state.values.filters]);

    const handleAddEdit = useCallback((row: IGovernanceItem | undefined) => {
        state.update('selectedRow', row);
        addEditDialog.handleOpen();

        // eslint-disable-next-line
    }, []);

    const handleRemove = useCallback((row: IGovernanceItem) => {
        state.update('selectedRow', row);
        removeDialog.handleOpen();

        // eslint-disable-next-line
    }, []);


    const dataGridColumns = [
        { 
            field: 'name', 
            headerName: translate('name'),
            flex: 1, 
            minWidth: 200
        }, { 
            field: 'roomTypes',  
            headerName: translate('roomTypes'), 
            flex: 1, 
            minWidth: 400,
            renderCell: (params: any) => {
                const roomTypes = params.row.roomTypes as number[];

                if (roomTypes.length === 0) {
                    return translate('all');
                }
                
                return enterprise?.partnerPms.roomTypes.filter(r => roomTypes.includes(+r.id)).map(r => r.description).join(', ');
            }
        }, { 
            field: 'required',  
            headerName: translate('required'), 
            flex: 1, 
            minWidth: 100,
            renderCell: (params: any) => {
                return translate(params.row.required ? 'yes' : 'no');
            }
        }, { 
            field: 'orderBy',  
            headerName: translate('ordering'), 
            flex: 1, 
            minWidth: 100
        }, { 
            field: 'registrationStatusId', 
            type: DefaultConstants.dataGridRegistrationStatusType, 
            headerName: translate('situation'), 
            flex: 1, 
            minWidth: 200, 
            renderCell: (params: any) => {
                return <RegistrationStatus id={params.row.registrationStatusId} />;
            }
        }, { 
            field: 'actions', 
            type: 'actions', 
            headerName: translate('actions'), 
            width: 200, 
            renderCell: (params: any) => {
                if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                    return;
                }

                return (
                    <Grid item>
                        {
                            hasPermission(`${linkProps.alias[0]}.Add`) && (
                                <Tooltip title={translate('clone')}>
                                    <IconButton 
                                        className='actionbutton-clone' 
                                        onClick={() => handleAddEdit({
                                            ...params.row, 
                                            id: 0,
                                            name: '',
                                            token: '',
                                        })}
                                    >
                                        <ContentCopy fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            hasPermission(`${linkProps.alias[0]}.Edit`) && (
                                <Tooltip title={translate('edit')}>
                                    <IconButton className='actionbutton-edit' onClick={() => handleAddEdit(params.row)}>
                                        <Edit fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }

                        {
                            hasPermission(`${linkProps.alias[0]}.Remove`) && (
                                <Tooltip title={translate('remove')}>
                                    <IconButton className='actionbutton-remove' onClick={() => handleRemove(params.row)}>
                                        <Delete fontSize='small' />
                                    </IconButton>
                                </Tooltip>
                            )
                        }
                    </Grid>
                );
            }
        }
    ];

    return (
        <>
            <Grid item xs={12} className='grid-header'>
                <Grid item className='grid-title'>
                    <ContentTitle {...linkProps} />
                </Grid>

                <Grid item className='grid-tools'>
                    {
                        hasPermission(`${linkProps.alias[0]}.Add`) && (
                            <Tooltip title={translate('add')}>
                                <IconButton onClick={() => handleAddEdit(undefined)}>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    
                    <Tooltip title={translate('filter')}>
                        <IconButton onClick={() => filtersDrawer.handleOpen()}>
                            <FilterAlt />
                        </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={translate('refresh')}>
                        <IconButton onClick={handleRefresh}>
                            <Refresh />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>

            <Grid container className='grid-content'>
                <Grid item xs={12} className='paper-content'>
                    <DataGrid 
                        columns={dataGridColumns} 
                        data={state.values.data} 
                        openSearch={false}
                        onCellDoubleClick={params => {
                            if (Number(params.row.registrationStatusId) === RegistrationStatusEnum.Excluded) {
                                return;
                            }

                            if (hasPermission(`${linkProps.alias[0]}.Edit`)) {
                                handleAddEdit(params.row);
                                return;
                            }
                            
                            if (hasPermission(`${linkProps.alias[0]}.Remove`)) {
                                handleRemove(params.row);
                                return;
                            }
                        }}
                    />
                </Grid>
            </Grid>

            <Filters
                open={filtersDrawer.open}
                handleClose={() => filtersDrawer.handleClose()}
                handleApplyFilters={(formData) => state.update('filters', formData)}
            />

            <AddEditDialog
                open={addEditDialog.open}
                handleClose={() => addEditDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
            />

            <RemoveDialog
                open={removeDialog.open}
                handleClose={() => removeDialog.handleClose()}
                data={state.values.selectedRow}
                handleListRefresh={handleRefresh}
            />
        </>
    );
}

export default GovernanceItem;